import React, { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Star, X } from "lucide-react";
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { toast } from "react-toastify";
import { UserContext } from "../context/UserContext";
import AuthModal from "../components/modal/AuthModal";

const RatingsReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedStars, setSelectedStars] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [reviewText, setReviewText] = useState("");
  const [loading, setLoading] = useState(false);

  const { user } = useContext(UserContext);

  const tagsList = ["Awesome", "Helpful", "Inspiring", "Fast", "Professional"];
  const ExpandableText = ({ text, maxChars = 120 }) => {
    const [expanded, setExpanded] = useState(false);
    const isLong = text.length > maxChars;
    const displayText = expanded ? text : text.slice(0, maxChars);

    return (
      <div>
        <p>
          {displayText}
          {!expanded && isLong ? "..." : ""}
        </p>
        {isLong && (
          <ViewMoreButton onClick={() => setExpanded(!expanded)}>
            {expanded ? "View Less" : "View More"}
          </ViewMoreButton>
        )}
      </div>
    );
  };

  // 🔹 Fetch reviews from Firebase
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const q = query(
          collection(db, "ratings"),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setReviews(data);
      } catch (err) {
        console.error("Error fetching reviews:", err);
      }
    };
    fetchReviews();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedStars === 0) {
      toast.error("Please select a star rating.");
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, "ratings"), {
        rating: selectedStars,
        tags: selectedTags,
        text: reviewText || "", // ✅ Optional review text
        name: user?.name || "Anonymous",
        image:
          user?.profilePic ||
          `https://api.dicebear.com/9.x/thumbs/svg?seed=${Math.random()}`,
        createdAt: serverTimestamp(),
        userId: user?.id || null,
      });
      toast.success("Thank you for your rating!");
      setShowModal(false);
      setSelectedStars(0);
      setSelectedTags([]);
      setReviewText("");

      // Refresh reviews
      const q = query(collection(db, "ratings"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setReviews(data);
    } catch (err) {
      console.error("Error adding review:", err);
      toast.error("Failed to add review.");
    }
    setLoading(false);
  };

  const handleRateClick = () => {
    if (!user) {
      setShowAuthModal(true);
    } else {
      setShowModal(true);
    }
  };

  return (
    <SectionWrapper id="reviews">
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        What <Highlight>Developers Say</Highlight>
      </motion.h2>

      <Subtitle>
        Hear from developers and freelancers growing with SolveMyCodeIssue.
      </Subtitle>

      {reviews.length === 0 ? (
        <NoReviewBox>
          <p>No reviews yet. Be the first to rate us!</p>
          <RateUsButton onClick={handleRateClick}>Rate Us ⭐</RateUsButton>
        </NoReviewBox>
      ) : (
        <>
          <Cards>
            {reviews.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Card>
                  <UserRow>
                    <Avatar src={r.image} alt={r.name} />
                    <UserInfo>
                      <h4>{r.name}</h4>
                      <Stars>
                        {Array.from({ length: r.rating }).map((_, idx) => (
                          <Star
                            key={idx}
                            size={16}
                            color="#ffd700"
                            fill="#ffd700"
                          />
                        ))}
                      </Stars>
                    </UserInfo>
                  </UserRow>
                  {r.text && <ExpandableText text={r.text} maxChars={120} />}
                  {r.tags?.length > 0 && (
                    <Tags>
                      {r.tags.map((t, idx) => (
                        <Tag key={idx}>{t}</Tag>
                      ))}
                    </Tags>
                  )}
                </Card>
              </motion.div>
            ))}
          </Cards>

          <RateUsButton onClick={handleRateClick}>Rate Us ⭐</RateUsButton>
        </>
      )}

      {showModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <h3>Rate SolveMyCodeIssue</h3>
              <CloseButton onClick={() => setShowModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <StarsSelect>
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={32}
                  onClick={() => setSelectedStars(i + 1)}
                  color={i < selectedStars ? "#ffd700" : "#555"}
                  fill={i < selectedStars ? "#ffd700" : "none"}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </StarsSelect>

            <TagSelector>
              {tagsList.map((tag) => (
                <TagOption
                  key={tag}
                  selected={selectedTags.includes(tag)}
                  onClick={() =>
                    setSelectedTags((prev) =>
                      prev.includes(tag)
                        ? prev.filter((t) => t !== tag)
                        : [...prev, tag]
                    )
                  }
                >
                  {tag}
                </TagOption>
              ))}
            </TagSelector>

            {/* ✨ Modern Review Input */}
            <ReviewInputWrapper>
              <ReviewTextArea
                placeholder="(Optional) Share your experience... ✨"
                value={reviewText}
                maxLength={300}
                onChange={(e) => setReviewText(e.target.value)}
              />
              <WordCount>{reviewText.length}/300</WordCount>
            </ReviewInputWrapper>

            <SubmitButton onClick={handleSubmit} disabled={loading}>
              {loading ? "Posting..." : "Submit Review"}
            </SubmitButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </SectionWrapper>
  );
};

export default RatingsReviewsSection;

/* ---------- Styles ---------- */
const SectionWrapper = styled.section`
  width: 100%;
  background: linear-gradient(180deg, #0b0617, #000);
  color: #fff;
  padding: 100px 20px;
  text-align: center;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #ccc;
  margin-top: 10px;
  margin-bottom: 40px;
`;
const ViewMoreButton = styled.button`
  background: none;
  border: none;
  color: #9b5cff;
  font-size: 0.85rem;
  cursor: pointer;
  margin-top: 4px;
  padding: 0;
  text-decoration: underline;
  transition: 0.3s;

  &:hover {
    color: #c59fff;
  }
`;

const NoReviewBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 40px;
  max-width: 500px;
  margin: 0 auto;
  backdrop-filter: blur(8px);
`;

const RateUsButton = styled.button`
  margin-top: 20px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  border: none;
  padding: 12px 24px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(155, 92, 255, 0.4);
  }
`;

/* ✅ Fix: make cards consistent width and aligned nicely */
const Cards = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 25px;
  justify-content: center;
  margin-top: 30px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  text-align: left;
  backdrop-filter: blur(8px);
  transition: all 0.3s;
  width: 320px;
  min-height: 220px; /* ✅ Consistent height */
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:hover {
    transform: translateY(-5px);
    border-color: #c59fff;
  }

  p {
    color: #ccc;
    font-size: 0.95rem;
    margin-top: 10px;
    line-height: 1.6;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
`;

const UserInfo = styled.div`
  h4 {
    font-size: 1rem;
    margin: 0;
  }
`;

const Stars = styled.div`
  display: flex;
  gap: 2px;
`;

const Tags = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  background: rgba(155, 92, 255, 0.2);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.8rem;
  color: #c59fff;
`;

/* -------- Modal Styles -------- */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1c1b22;
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 20px;
  padding: 30px;
  width: 90%;
  max-width: 500px;
  color: #fff;
  text-align: center;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #aaa;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    color: #fff;
  }
`;

const StarsSelect = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const TagSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const TagOption = styled.span`
  background: ${({ selected }) =>
    selected ? "rgba(155,92,255,0.5)" : "rgba(255,255,255,0.1)"};
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 10px;
  padding: 6px 12px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: 0.3s;
  color: ${({ selected }) => (selected ? "#fff" : "#ccc")};

  &:hover {
    background: rgba(155, 92, 255, 0.4);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: 0.3s;

  &:hover {
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ReviewInputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin: 20px 0 10px;
`;

const ReviewTextArea = styled.textarea`
  width: 90%;
  min-height: 120px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 14px;
  padding: 14px 16px;
  color: #fff;
  font-size: 0.95rem;
  font-family: "Inter", sans-serif;
  line-height: 1.6;
  resize: none;
  outline: none;
  transition: 0.3s ease all;
  box-shadow: 0 0 0 rgba(155, 92, 255, 0);

  &::placeholder {
    color: #aaa;
    font-style: italic;
  }

  &:focus {
    border-color: #c59fff;
    box-shadow: 0 0 12px rgba(155, 92, 255, 0.4);
    background: rgba(255, 255, 255, 0.07);
  }
`;

const WordCount = styled.span`
  position: absolute;
  bottom: 10px;
  right: 16px;
  font-size: 0.75rem;
  color: #999;
`;
